import { Alert, Col, Descriptions, Empty, Row, Space, Tabs, TabsProps, Tag, message } from "antd";
import { getOrderByNumber } from "api/coreapi/orders";
import LoadingSpinner from "components/common/loadingSpinner/loadingSpinner";
import { PageTitle } from "components/common/pageTitle/pageTitle";
import ErrorMessage from "components/errorMessage";
import { CustomerStatuses } from "constants/customers";
import { InternalOrderTaskTypes, OrderManagementModes, OrderStatuses } from "constants/orders";
import { OrderPaymentStatuses, PaymentTypes } from "constants/payments";
import { PermissionList } from "constants/permissions";
import { SupplierStatuses } from "constants/suppliers";
import usePermission from "features/auth/usePermission";
import CancelOrderButton from "features/orders/cancelOrderButton";
import OrderCustomerDetail from "features/orders/orderCustomerDetail";
import { OrderForm } from "features/orders/orderForm";
import OrderQuoteDetail from "features/orders/orderQuoteDetail";
import OrderQuotesReview from "features/orders/orderQuotesReview";
import OrderSupplierDetail from "features/orders/orderSupplierDetail";
import OrderExecutionActions from "features/orders/orderExecutionActions";
import RetrySupplierConfirmationModal from "features/orders/retrySupplierConfirmationModal";
import OrderPaymentButton from "features/payments/orderPaymentButton";
import PaymentList from "features/payments/paymentList";
import { useAppSelector } from "hooks/useAppSelector";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Order } from "shared/models/orderModel";
import dayjs from 'dayjs';
import { formatMoney } from "utils/moneyUtils";

function OrderDetailPage() {

    const { orderNumber } = useParams();
    const [loading, setLoading] = useState(false);
    const [order, setOrder] = useState<Order>();
    const userCanEditEmployees = usePermission([PermissionList.EmployeeList]);
    const userCanCreateOrder = usePermission([PermissionList.OrderCreate]);
    const userCanAcceptCustomerOrder = usePermission([PermissionList.OrderQuoteAccept]);
    const userCanManageInternalOrder = usePermission([PermissionList.OrderManageInternal]);
    const userHasCancelOrderPermission = usePermission([PermissionList.OrderCancel]);
    const currentUser = useAppSelector(state => state.currentUser);
    const isCustomerUser = currentUser?.customer?.customerStatusId === CustomerStatuses.Active;
    const isSupplierUser = currentUser?.supplier?.supplierStatusId === SupplierStatuses.Active;
    const isInternalUser = usePermission([PermissionList.OrderInternalView]);
    const isInternallyManaged = order?.managementModeId === OrderManagementModes.InternallyManaged;
    const userCanAcceptOrder = userCanAcceptCustomerOrder || (userCanManageInternalOrder && isInternallyManaged);
    const userCanCancelOrder = userHasCancelOrderPermission || (userCanManageInternalOrder && isInternallyManaged);


    const getInfo = useCallback(async () => {
        if (!orderNumber) {
            return;
        }

        try {
            setLoading(true);
            const res = await getOrderByNumber(orderNumber);
            setOrder(res.result);
        } catch (error: any) {
            if (error.errors) {
                message.error(ErrorMessage(error.errors));
            } else {
                message.error('Error obteniendo información de la orden de servicio.');
            }

        } finally {
            setLoading(false);
        }
    }, [orderNumber]);

    useEffect(() => {
        getInfo();
    }, [orderNumber, getInfo]);

    if (loading) {
        return (<LoadingSpinner />);
    }

    let items: TabsProps['items'] = [
        {
            key: 'service',
            label: `Servicio`,
            children: <Row justify={"center"}>
                <Col md={{ span: 16 }} span={24}>
                    <OrderForm
                        context={isInternallyManaged ? 'administrative' : 'customer'}
                        order={order} />
                </Col>
            </Row>,
        },
    ];

    if (order?.orderStatusId !== OrderStatuses.Canceled) {
        if (order?.supplier &&
            (
                isInternalUser ||
                isSupplierUser ||
                (isCustomerUser && order.isSupplierInfoVisible)
            )) {
            items = [...items, {
                key: 'supplier',
                label: `Proveedor`,
                children: <Row justify={"center"}>
                    <Col md={{ span: 16 }} span={24}>
                        <OrderSupplierDetail
                            orderSupplier={order.supplier}
                            canEdit={userCanEditEmployees}
                            orderNumber={order.orderNumber} />
                    </Col>
                </Row>,
            }]
        }

        if (order &&
            (isInternalUser ||
                isCustomerUser ||
                (order.supplier && isSupplierUser)
            )
        ) {
            items = [...items, {
                key: 'customer',
                label: `Cliente`,
                children: <Row justify={"center"}>
                    <Col md={{ span: 16 }} span={24}>
                        <OrderCustomerDetail
                            canEdit={userCanCreateOrder}
                            orderNumber={order!.orderNumber} />
                    </Col>
                </Row>,
            }]
        }

        if (order?.acceptedOrderQuoteId ||
            (order?.orderStatusId === OrderStatuses.PendingSupplierSelection && userCanAcceptOrder)) {
            items = [...items, {
                key: 'quote',
                label: `Facturación`,
                children: <Row justify={"center"}>
                    <Col md={{ span: 16 }} span={24}>

                        {order.acceptedOrderQuoteId &&
                            <Space direction="vertical">
                                <OrderQuoteDetail
                                    orderQuoteId={order.acceptedOrderQuoteId}
                                />

                                {order.payments && order.payments.length > 0 &&
                                    <PaymentList payments={order.payments}></PaymentList>}


                            </Space>

                        }

                        {!order.acceptedOrderQuoteId &&
                            order?.orderStatusId === OrderStatuses.PendingSupplierSelection
                            && userCanAcceptOrder &&
                            <OrderQuotesReview
                                orderNumber={order.orderNumber.toString()}
                                onQuoteAccepted={() => window.location.reload()} />
                        }
                    </Col>
                </Row>,
            }]
        }
    }

    if (loading) {
        return <LoadingSpinner></LoadingSpinner>
    }

    if (!order) {
        return <Empty />
    }

    return (
        <>
            <PageTitle>
                Orden # {orderNumber} - {order?.orderStatusName}
                {isInternallyManaged && <Tag color="blue" style={{ marginLeft: 12 }}>Gestionada internamente</Tag>}
                <Row justify={"center"}>
                    <OrderExecutionActions order={order} onTransitioned={getInfo} />
                    {userCanCancelOrder && order.orderStatusId !== OrderStatuses.Canceled &&
                        <CancelOrderButton orderNumber={order.orderNumber} onCanceled={async () => await getInfo()}></CancelOrderButton>
                    }
                </Row>

            </PageTitle>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 3 }} style={{ marginBottom: 20 }}>
                <Descriptions.Item label="Modalidad de gestión">
                    {isInternallyManaged ? 'Interna' : 'Cliente'}
                </Descriptions.Item>
                <Descriptions.Item label="Inicio real">
                    {order.actualStartDate
                        ? dayjs(order.actualStartDate).format('DD-MM-YYYY hh:mm A')
                        : 'Sin registrar'}
                </Descriptions.Item>
                <Descriptions.Item label="Fin real">
                    {order.actualEndDate
                        ? dayjs(order.actualEndDate).format('DD-MM-YYYY hh:mm A')
                        : 'Sin registrar'}
                </Descriptions.Item>
            </Descriptions>
            {order.orderStatusId === OrderStatuses.ConfirmingAvailability &&

                <Alert type="info"
                    style={{ marginBottom: 20 }}
                    showIcon
                    description={`Tiempo estimado para confirmación de disponibilidad de proveedores: ${dayjs(order.quoteDeadline).format('MMMM D, h:mm A')}`} />
            }

            {isInternallyManaged && order.openInternalAction &&
                <Alert
                    type={order.openInternalAction.deadline && dayjs(order.openInternalAction.deadline).isBefore(dayjs()) ? 'error' : 'warning'}
                    showIcon
                    style={{ marginBottom: 20 }}
                    message={`Acción pendiente: ${order.openInternalAction.typeName}`}
                    description={order.openInternalAction.deadline
                        ? `Vencimiento: ${dayjs(order.openInternalAction.deadline).format('DD-MM-YYYY hh:mm A')}`
                        : 'Esta acción no tiene vencimiento definido.'}
                    action={[
                        InternalOrderTaskTypes.ResolveNoEligibleSuppliers,
                        InternalOrderTaskTypes.ResolveNoConfirmedQuotes,
                    ].includes(order.openInternalAction.typeId)
                        ? <RetrySupplierConfirmationModal orderNumber={order.orderNumber} onRetried={getInfo} />
                        : undefined} />
            }

            {
                order.paymentTypeId === PaymentTypes.Transfer
                && order.managementModeId === OrderManagementModes.CustomerManaged
                && order.orderPaymentStatusId === OrderPaymentStatuses.Pending
                && order.orderStatusId === OrderStatuses.PendingPaymentConfirmation
                && isCustomerUser
                &&
                <Alert type="warning" message={
                    <div>
                        Para dar inicio al proceso y acceder a los detalles del proveedor, es necesario completar el pago del servicio. Esto garantizará una experiencia continua y sin contratiempos.



                        <OrderPaymentButton orderNumber={order.orderNumber}></OrderPaymentButton>
                    </div>
                } showIcon />

            }

            {isInternallyManaged && order.paymentTypeId === PaymentTypes.Credit && order.acceptedOrderQuoteId &&
                <Alert
                    type={order.orderPaymentStatusId === OrderPaymentStatuses.Pending ? "warning" : "info"}
                    showIcon
                    style={{ marginBottom: 20 }}
                    message={order.orderPaymentStatusId === OrderPaymentStatuses.Pending ? "Pendiente de cobro" : order.paymentTypeName}
                    description={
                        <Space direction="vertical">
                            {order.acceptedOrderQuoteTotal != null &&
                                <strong>Total aceptado: {formatMoney(order.acceptedOrderQuoteTotal.toString())}</strong>}
                            <span>La orden puede continuar sin pago inmediato.</span>
                        </Space>
                    } />
            }


            <Tabs
                defaultActiveKey="service"
                items={items}
                type="card">
            </Tabs>
        </>
    );

}

export default OrderDetailPage;
